import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { CrudEntry } from "@/lib/mma/types.gen";
import { formatResourceLabel } from "@/lib/utils";
import { useMemo } from "react";

const ALL_RESOURCES = [
  "GATEWAY",
  "DIVERGENCE",
  "LICENCE",
  "USER",
  "ORGANISATION",
  "BUSINESS_DIRECTORY",
  "USER_LABEL",
  "QUEUE",
  "QUEUE_GROUP",
  "CALL_SCREENING",
  "IVR",
  "SETTINGS",
  "ADMIN_USER",
  "ADMIN_PROFILES",
  "EMAILS",
  "CLIENT",
];

const FULL_PERMISSIONS: CrudEntry = ["C", "R", "U", "D"];

export function PermissionsDialog({
  accessControl,
  profile,
  open,
  setOpen,
}: {
  accessControl: Record<string, CrudEntry>;
  profile?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
}) {
  const filteredPermissions = useMemo(() => {
    let permissions: [string, CrudEntry][];

    if (profile === "0") {
      permissions = ALL_RESOURCES.map((resource) => [resource, FULL_PERMISSIONS]);
    } else {
      permissions = Object.entries(accessControl).filter(([_, perms]) => perms.length > 0);
    }

    return permissions.sort(([resourceA], [resourceB]) => {
      const labelA = formatResourceLabel(resourceA);
      const labelB = formatResourceLabel(resourceB);
      return labelA.localeCompare(labelB);
    });
  }, [accessControl, profile]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90vh] w-full max-w-2xl lg:max-w-4xl grid grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold pb-4">Your Permissions</DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto">
          {filteredPermissions.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center">You don't have any permissions assigned.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Resource</TableHead>
                  <TableHead>All</TableHead>
                  <TableHead>Read</TableHead>
                  <TableHead>Write</TableHead>
                  <TableHead>Update</TableHead>
                  <TableHead>Delete</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPermissions.map(([resource, permissions]) => (
                  <TableRow key={resource}>
                    <TableCell>{formatResourceLabel(resource)}</TableCell>
                    <TableCell>
                      <Checkbox checked={permissions.length === 4} disabled />
                    </TableCell>
                    <TableCell>
                      <Checkbox checked={permissions.includes("R")} disabled />
                    </TableCell>
                    <TableCell>
                      <Checkbox checked={permissions.includes("C")} disabled />
                    </TableCell>
                    <TableCell>
                      <Checkbox checked={permissions.includes("U")} disabled />
                    </TableCell>
                    <TableCell>
                      <Checkbox checked={permissions.includes("D")} disabled />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button onClick={() => setOpen(false)}>Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
